import { useState } from 'react'
import { lumi } from '@/lib/lumi'

export interface Training {
  _id: string
  title: string
  type: 'curso' | 'taller' | 'seminario' | 'certificacion'
  status: 'borrador' | 'publicada' | 'finalizada' | 'cancelada'
  description?: string
  instructor?: string
  modality?: 'presencial' | 'en_linea' | 'hibrida'
  duration?: string
  cost?: number
  capacity?: number
  enrolledCount?: number
  startDate?: string
  endDate?: string
  tags?: string[]
  imageUrl?: string
  videoUrl?: string
  pdfUrl?: string
  location?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Enrollment {
  _id: string
  trainingId: string
  trainingTitle?: string
  trainingType?: string
  collaboratorUserId: string
  collaboratorName?: string
  status: 'inscrito' | 'en_progreso' | 'completado' | 'cancelado'
  completedAt?: string
  certificateUrl?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export const TRAINING_TYPE_LABELS: Record<Training['type'], string> = {
  curso: 'Curso',
  taller: 'Taller',
  seminario: 'Seminario',
  certificacion: 'Certificación',
}

export const TRAINING_MODALITY_LABELS: Record<string, string> = {
  presencial: 'Presencial',
  en_linea: 'En línea',
  hibrida: 'Híbrida',
}

export const TRAINING_STATUS_META: Record<Training['status'], { label: string; color: string }> = {
  borrador: { label: 'Borrador', color: 'bg-slate-100 text-slate-600' },
  publicada: { label: 'Publicada', color: 'bg-emerald-100 text-emerald-700' },
  finalizada: { label: 'Finalizada', color: 'bg-blue-100 text-blue-700' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
}

export const ENROLLMENT_STATUS_META: Record<Enrollment['status'], { label: string; color: string }> = {
  inscrito: { label: 'Inscrito', color: 'bg-amber-100 text-amber-700' },
  en_progreso: { label: 'En progreso', color: 'bg-blue-100 text-blue-700' },
  completado: { label: 'Completado', color: 'bg-emerald-100 text-emerald-700' },
  cancelado: { label: 'Cancelado', color: 'bg-slate-100 text-slate-500' },
}

export function useTrainings() {
  const [trainings, setTrainings] = useState<Training[]>([])
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPublished = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await lumi.entities.trainings.list({
        filter: { status: 'publicada' },
        sort: { startDate: 1 },
        limit: 100,
      })
      setTrainings(res.list as Training[])
    } catch (e) {
      console.error('[useTrainings] fetchPublished error:', e)
      setError('No se pudieron cargar las capacitaciones.')
    } finally {
      setLoading(false)
    }
  }

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await lumi.entities.trainings.list({
        sort: { createdAt: -1 },
        limit: 200,
      })
      setTrainings(res.list as Training[])
    } catch (e) {
      console.error('[useTrainings] fetchAll error:', e)
      setError('No se pudieron cargar las capacitaciones.')
    } finally {
      setLoading(false)
    }
  }

  const createTraining = async (data: Omit<Training, '_id'>) => {
    const now = new Date().toISOString()
    const doc = { ...data, createdAt: now, updatedAt: now }
    const res = await lumi.entities.trainings.create(doc)
    return res as Training
  }

  const updateTraining = async (id: string, data: Partial<Training>) => {
    const now = new Date().toISOString()
    await lumi.entities.trainings.update(id, { ...data, updatedAt: now })
  }

  const deleteTraining = async (id: string) => {
    await lumi.entities.trainings.delete(id)
  }

  // ---- Enrollments ----

  const fetchMyEnrollments = async (collaboratorUserId: string) => {
    setLoading(true)
    try {
      const res = await lumi.entities.enrollments.list({
        filter: { collaboratorUserId },
        sort: { createdAt: -1 },
        limit: 200,
      })
      setEnrollments(res.list as Enrollment[])
    } catch (e) {
      console.error('[useTrainings] fetchMyEnrollments error:', e)
    } finally {
      setLoading(false)
    }
  }

  const enroll = async (training: Training, collaboratorUserId: string, collaboratorName: string) => {
    const now = new Date().toISOString()
    const doc = {
      trainingId: training._id,
      trainingTitle: training.title,
      trainingType: training.type,
      collaboratorUserId,
      collaboratorName,
      status: 'inscrito' as const,
      creator: collaboratorUserId,
      createdAt: now,
      updatedAt: now,
    }
    const res = await lumi.entities.enrollments.create(doc)
    return res as Enrollment
  }

  const updateEnrollment = async (id: string, data: Partial<Enrollment>) => {
    const now = new Date().toISOString()
    await lumi.entities.enrollments.update(id, { ...data, updatedAt: now })
  }

  return {
    trainings,
    enrollments,
    loading,
    error,
    fetchPublished,
    fetchAll,
    createTraining,
    updateTraining,
    deleteTraining,
    fetchMyEnrollments,
    enroll,
    updateEnrollment,
  }
}
